import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import BASE_URL from "../../config";
import { fetchWithAuth, xhrWithAuth } from "../../utils/authUtils";
import Cookies from "js-cookie";
import { readErrorResponse } from "../../utils/readErrorResponse";

const extractErrorMessage = (payload, fallbackError) => {
  if (!payload) return fallbackError || "Something went wrong";
  if (typeof payload === "string") return payload;
  if (payload.message) return payload.message;
  if (payload.error) {
    if (typeof payload.error === "string") return payload.error;
    if (payload.error.message) return payload.error.message;
  }
  if (payload.detail) return payload.detail;
  
  if (payload.responseText) {
    try {
      const parsed = JSON.parse(payload.responseText);
      return extractErrorMessage(parsed, fallbackError);
    } catch {
      if (payload.responseText.length < 150) {
        return payload.responseText;
      }
    }
  }

  if (typeof payload === "object") {
    const keys = Object.keys(payload);
    if (keys.length > 0) {
      const firstVal = payload[keys[0]];
      if (Array.isArray(firstVal) && typeof firstVal[0] === "string") {
        return `${keys[0]}: ${firstVal[0]}`;
      }
      if (typeof firstVal === "string") {
        return `${keys[0]}: ${firstVal}`;
      }
    }
  }

  if (payload.status) {
    return `Request failed with status ${payload.status} (${payload.statusText || 'Unknown Error'})`;
  }

  return fallbackError || "Something went wrong";
};

const sanitizeErrorDetails = (payload) => {
  if (!payload) return null;
  const rawDetails = {
    url: payload.url,
    status: payload.status,
    statusText: payload.statusText,
    responseText: payload.responseText ? payload.responseText.substring(0, 1000) : null,
    timestamp: new Date().toISOString()
  };

  if (rawDetails.responseText && typeof rawDetails.responseText === "string") {
    // Scrub secret JWT signatures completely from logs before storing them in state
    rawDetails.responseText = rawDetails.responseText.replace(
      /Bearer\s[A-Za-z0-9-_=]+\.[A-Za-z0-9-_=]+\.?[A-Za-z0-9-_.+/=]*/g,
      "Bearer [SCRUBBED]"
    );
  }
  return rawDetails;
};

const initialState = {
  singleProduct: null,
  relatedProducts: [],
  relatedStatus: "idle",
  status: {
    fetchProductStatus: "idle",
    downloadProductStatus: null,
    uploadProductStatus: null, // for upload
    uploadProgress: 0,
    uploadLoaded: 0,
    uploadTotal: 0,
  },
  error: {
    fetchProductError: null,
    downloadProductError: null,
    uploadProductError: null, // for upload errors
    uploadProductErrorDetails: null,
  },
};

// fetching  by ID
export const fetchSingleProduct = createAsyncThunk(
  "singleProduct/fetchSingleProduct",
  async (id, { rejectWithValue }) => {
    try {
      const accessToken = Cookies.get('access_token');
      let response;
      if (accessToken) {
        response = await fetchWithAuth(`${BASE_URL}/products/v1/products/slug/${id}/`, {
          method: "GET",
        });
      } else {
        response = await fetch(`${BASE_URL}/products/v1/products/slug/${id}/`, {
          method: "GET",
        });
      }

      if (!response.ok) {
        const errorPayload = await readErrorResponse(response);
        return rejectWithValue({
          status: response.status,
          data: errorPayload,
        });
      }

      return response.json();
    } catch (error) {
      return rejectWithValue({
        status: null,
        data: error.message,
      });
    }
  }
);

export const fetchRelatedProducts = createAsyncThunk(
  "singleProduct/fetchRelatedProducts",
  async ({ slug, pageSize = 4 }, { rejectWithValue }) => {
    try {
      const response = await fetch(
        `${BASE_URL}/products/v1/products/${slug}/related/?page_size=${pageSize}`
      );
      if (!response.ok) return rejectWithValue({ status: response.status });
      return response.json();
    } catch (error) {
      return rejectWithValue({ status: null, data: error.message });
    }
  }
);

// Polls an async download job. Resolves with the file URL once the job is
// ready, rejects on "failed" or after the timeout. The signal arg allows the
// thunk to be cancelled.
const pollDownloadJob = async (jobId, { signal, timeoutMs = 120000, intervalMs = 1800 } = {}) => {
  const deadline = Date.now() + timeoutMs;
  const url = `${BASE_URL}/products/v1/download-jobs/${jobId}/`;
  while (true) {
    if (signal && signal.aborted) {
      throw new DOMException("Polling cancelled", "AbortError");
    }
    const response = await fetchWithAuth(url, { method: "GET", signal });
    if (response.ok) {
      const data = await response.json();
      if (data.status === "ready") {
        // Build the file URL ourselves from BASE_URL (https). Never trust an absolute
        // file_url from the server: behind a TLS-terminating proxy build_absolute_uri
        // can return http://, which the https page blocks as mixed content.
        return `${BASE_URL}/products/v1/download-jobs/${jobId}/file/`;
      }
      if (data.status === "failed") {
        throw new Error(data.error || "Download preparation failed");
      }
    } else if (response.status === 403) {
      throw new Error("You do not have access to this download.");
    }
    if (Date.now() >= deadline) {
      throw new Error("Download timed out. Please try again.");
    }
    await new Promise((r) => setTimeout(r, intervalMs));
  }
};

// Downloading asset
export const downloadAsset = createAsyncThunk(
  "singleProduct/downloadAsset",
  async ({ assetId, assetName, productId }, { rejectWithValue, signal }) => {
    if (assetId === undefined || assetId === null) {
      return rejectWithValue("Asset identifier is missing.");
    }

    const normalizedId = String(assetId).trim();
    if (!normalizedId || normalizedId.toLowerCase() === "undefined" || normalizedId.toLowerCase() === "null") {
      return rejectWithValue("Asset identifier is invalid.");
    }

    const sanitizeName = (value) => {
      if (typeof value !== "string") return null;
      const trimmed = value.trim();
      if (!trimmed || trimmed.toLowerCase() === "undefined") return null;
      return trimmed.replace(/[\\/:*?"<>|]+/g, "_");
    };

    const normalizedName = sanitizeName(assetName) || `asset-${normalizedId}`;

    try {
      const encodedFilename = encodeURIComponent(normalizedName);
      const response = await fetchWithAuth(`${BASE_URL}/products/v1/download-asset/${normalizedId}/${encodedFilename}/`, {
        method: "GET",
        signal,
      });

      if (!response.ok) {
        const errorDetails = await readErrorResponse(response);
        return rejectWithValue(errorDetails);
      }

      // 202 -> async job. Poll until ready, then fetch the file. The free-asset
      // POST is deferred to AFTER we have a real file, so a free purchase is
      // never recorded for a download the user never actually received.
      if (response.status === 202) {
        const { job_id: jobId } = await response.json();
        const fileUrl = await pollDownloadJob(jobId, { signal });
        const fileResp = await fetchWithAuth(fileUrl, { method: "GET", signal });
        if (!fileResp.ok) {
          const errorDetails = await readErrorResponse(fileResp);
          return rejectWithValue(errorDetails);
        }
        if (productId) {
          try {
            const addFreeAssetResponse = await fetchWithAuth(`${BASE_URL}/products/v1/add-free-asset/${productId}/`, {
              method: "POST",
              signal,
            });
            if (!addFreeAssetResponse.ok) {
              const addFreeAssetError = await readErrorResponse(addFreeAssetResponse);
              console.warn("Failed to record free asset purchase:", addFreeAssetError);
            }
          } catch (error) {
            if (error instanceof DOMException && error.name === "AbortError") throw error;
            console.warn("Failed to record free asset purchase:", error);
          }
        }
        const blob = await fileResp.blob();
        const disposition = fileResp.headers.get("Content-Disposition");
        let filename = normalizedName;
        if (disposition) {
          const match = disposition.match(/filename\s*=\s*"?(?<name>[^";]+)"?/i);
          if (match && match.groups?.name) filename = match.groups.name.trim();
        }
        if (!filename || !/\.[A-Za-z0-9]{1,6}$/.test(filename)) {
          filename = `${normalizedName}.zip`;
        }
        return { blob, filename };
      }

      // 200 -> inline path (text/.3de/unsupported). Free-asset recorded here, as before.
      if (productId) {
        try {
          const addFreeAssetResponse = await fetchWithAuth(`${BASE_URL}/products/v1/add-free-asset/${productId}/`, {
            method: "POST",
            signal,
          });
          if (!addFreeAssetResponse.ok) {
            const addFreeAssetError = await readErrorResponse(addFreeAssetResponse);
            console.warn("Failed to record free asset purchase:", addFreeAssetError);
          }
        } catch (error) {
          if (error instanceof DOMException && error.name === "AbortError") throw error;
          console.warn("Failed to record free asset purchase:", error);
        }
      }

      const blob = await response.blob();
      const filename =
        normalizedName && /\.[A-Za-z0-9]{1,6}$/.test(normalizedName)
          ? normalizedName
          : `${normalizedName}.zip`;

      return { blob, filename };
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") {
        return rejectWithValue({ aborted: true, message: "Download was cancelled" });
      }
      return rejectWithValue(error.message);
    }
  }
);

export const downloadProductAssetsArchive = createAsyncThunk(
  "singleProduct/downloadProductAssetsArchive",
  async ({ productId, slug, formatName }, { rejectWithValue, signal }) => {
    try {
      if (!productId) {
        return rejectWithValue("Product identifier is required for archive download.");
      }

      const query = new URLSearchParams();
      if (typeof formatName === "string" && formatName.trim()) {
        query.set("format", formatName.trim());
      }

      const requestUrl = `${BASE_URL}/products/v1/download-all-assets/${productId}/${query.toString() ? `?${query.toString()}` : ""}`;

      const response = await fetchWithAuth(requestUrl, {
        method: "GET",
        signal,
      });

      if (!response.ok) {
        const errorDetails = await readErrorResponse(response);
        return rejectWithValue(errorDetails);
      }

      // Bundle is always async (jpeglib work). Poll, then fetch the file.
      const { job_id: jobId } = await response.json();
      const fileUrl = await pollDownloadJob(jobId, { signal });
      const fileResp = await fetchWithAuth(fileUrl, { method: "GET", signal });
      if (!fileResp.ok) {
        const errorDetails = await readErrorResponse(fileResp);
        return rejectWithValue(errorDetails);
      }

      const disposition = fileResp.headers.get("Content-Disposition");
      let filename = null;
      if (disposition) {
        const match = disposition.match(/filename\s*=\s*"?(?<name>[^";]+)"?/i);
        if (match && match.groups?.name) {
          filename = match.groups.name.trim();
        }
      }

      if (!filename) {
        const base = slug || productId;
        filename = `${base}-assets.zip`;
      }

      const blob = await fileResp.blob();
      return { blob, filename };
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") {
        return rejectWithValue({ aborted: true, message: "Download was cancelled" });
      }
      return rejectWithValue(error.message);
    }
  }
);

// Uploading form and file
export const uploadProductFile = createAsyncThunk(
  "singleProduct/uploadProductFile",
  async ({ formData, file }, { dispatch, rejectWithValue, signal }) => {
    try {
      const uploadData = new FormData();
      for (const [key, value] of Object.entries(formData)) {
        uploadData.append(key, value);
      }
      
      const fileFormat = file.name.split('.').pop().toLowerCase();
      uploadData.append("file", file);
      uploadData.append("fileFormat", fileFormat);

      // Force a 1-hour safe buffer for files larger than 50MB to maximize token lifetime
      const oneHourMs = 3600000;
      const dynamicBufferMs = file.size > 50 * 1024 * 1024 ? oneHourMs : 600000;

      const response = await xhrWithAuth(`${BASE_URL}/products/v1/contributions/`, {
        method: "POST",
        body: uploadData,
        signal,
        uploadBufferMs: dynamicBufferMs,
        onProgress: (percent, loaded, total) => {
          dispatch(setUploadProgress({ percent, loaded, total }));
        }
      });

      return response;
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") {
        return rejectWithValue({ aborted: true, message: "Upload was cancelled by user" });
      }
      return rejectWithValue(error);
    }
  }
);



const singleProductSlice = createSlice({
  name: "singleProduct",
  initialState,
  reducers: {
    setUploadProgress: (state, action) => {
      const { percent, loaded, total } = action.payload;
      state.status.uploadProgress = percent;
      state.status.uploadLoaded = loaded;
      state.status.uploadTotal = total;
    },
    clearUploadError: (state) => {
      state.error.uploadProductError = null;
      state.error.uploadProductErrorDetails = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch single product
      .addCase(fetchSingleProduct.pending, (state) => {
        state.status.fetchProductStatus = "loading";
        state.error.fetchProductError = null;
        state.relatedProducts = [];
        state.relatedStatus = "idle";
      })
      .addCase(fetchSingleProduct.fulfilled, (state, action) => {
        state.status.fetchProductStatus = "succeeded";
        state.singleProduct = action.payload;
        state.error.fetchProductError = null;
      })
      .addCase(fetchSingleProduct.rejected, (state, action) => {
        state.status.fetchProductStatus = "failed";
        state.error.fetchProductError = action.payload;
      })

      // Download asset
      .addCase(downloadAsset.pending, (state) => {
        state.status.downloadProductStatus = "loading";
        state.error.downloadProductError = null;
      })
      .addCase(downloadAsset.fulfilled, (state, action) => {
        state.status.downloadProductStatus = "succeeded";
        state.error.downloadProductError = null;

      const blob = action.payload?.blob;
      const originalFilename = action.payload?.filename;

      if (!(blob instanceof Blob)) {
        console.error("downloadAsset: invalid payload", action.payload);
        return;
      }

      const productInfo = state.singleProduct?.singleProduct || state.singleProduct;
      const assetId = action.meta?.arg?.assetId;
      const sanitizeName = (name) => {
        if (typeof name !== "string") return null;
        const trimmed = name.trim();
        if (!trimmed) return null;
        return trimmed.replace(/[\\/:*?"<>|]+/g, "_");
      };

      const baseName =
        sanitizeName(originalFilename) ||
        sanitizeName(productInfo?.slug ? `${productInfo.slug}-asset` : "") ||
        sanitizeName(assetId ? `asset-${assetId}` : "");

      const safeFilename =
        baseName && /\.[A-Za-z0-9]{1,6}$/.test(baseName)
          ? baseName
          : `${(baseName || `asset-${assetId || "file"}`)}.zip`;

      try {
        if (window.navigator && typeof window.navigator.msSaveOrOpenBlob === "function") {
          window.navigator.msSaveOrOpenBlob(blob, safeFilename);
          return;
        }

        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", safeFilename);
        link.style.display = "none";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => URL.revokeObjectURL(url), 2000);
      } catch (error) {
        console.error("downloadAsset: download failed", error);
      }
      })
      .addCase(downloadAsset.rejected, (state, action) => {
        state.status.downloadProductStatus = "failed";
        state.error.downloadProductError = action.payload;
      })
      .addCase(downloadProductAssetsArchive.pending, (state) => {
        state.status.downloadProductStatus = "loading";
        state.error.downloadProductError = null;
      })
      .addCase(downloadProductAssetsArchive.fulfilled, (state, action) => {
        state.status.downloadProductStatus = "succeeded";
        state.error.downloadProductError = null;

        const blob = action.payload?.blob;
        const originalFilename = action.payload?.filename;

        if (!(blob instanceof Blob)) {
          console.error("downloadProductAssetsArchive: invalid payload", action.payload);
          return;
        }

        const productInfo = state.singleProduct?.singleProduct || state.singleProduct;
        const arg = action.meta?.arg || {};
        const base =
          (typeof originalFilename === "string" && originalFilename.trim()) ||
          arg.slug ||
          productInfo?.slug ||
          arg.productId ||
          productInfo?.id ||
          "product";
        const safeFilename = base.endsWith(".zip") ? base : `${base}.zip`;

        try {
          if (window.navigator && typeof window.navigator.msSaveOrOpenBlob === "function") {
            window.navigator.msSaveOrOpenBlob(blob, safeFilename);
            return;
          }

          const url = URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", safeFilename);
          link.style.display = "none";
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          setTimeout(() => URL.revokeObjectURL(url), 2000);
        } catch (error) {
          console.error("downloadProductAssetsArchive: download failed", error);
        }
      })
      .addCase(downloadProductAssetsArchive.rejected, (state, action) => {
        state.status.downloadProductStatus = "failed";
        state.error.downloadProductError = action.payload;
      })

      // Related products
      .addCase(fetchRelatedProducts.pending, (state) => {
        state.relatedStatus = "loading";
      })
      .addCase(fetchRelatedProducts.fulfilled, (state, action) => {
        state.relatedStatus = "succeeded";
        state.relatedProducts = action.payload;
      })
      .addCase(fetchRelatedProducts.rejected, (state) => {
        state.relatedStatus = "failed";
        state.relatedProducts = [];
      })

      // Upload product file
      .addCase(uploadProductFile.pending, (state) => {
        state.status.uploadProductStatus = "loading";
        state.status.uploadProgress = 0;
        state.status.uploadLoaded = 0;
        state.status.uploadTotal = 0;
        state.error.uploadProductError = null;
        state.error.uploadProductErrorDetails = null;
      })
      .addCase(uploadProductFile.fulfilled, (state) => {
        state.status.uploadProductStatus = "succeeded";
        state.status.uploadProgress = 100;
      })
      .addCase(uploadProductFile.rejected, (state, action) => {
        if (action.payload?.aborted) {
          state.status.uploadProductStatus = "idle";
          state.status.uploadProgress = 0;
          return;
        }
        state.status.uploadProductStatus = "failed";
        
        // Extract the best readable error message from payload
        state.error.uploadProductError = extractErrorMessage(action.payload, action.error?.message);
        
        // Securely sanitize technical error metadata before saving it to store
        state.error.uploadProductErrorDetails = sanitizeErrorDetails(action.payload);
      });
  },
});

export const { setUploadProgress, clearUploadError } = singleProductSlice.actions;
export default singleProductSlice.reducer;
